import React from 'react';
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from 'primereact/avatargroup';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import { TabView, TabPanel } from 'primereact/tabview';
import { ScrollTop } from 'primereact/scrolltop';

import SectionCard from '../../../components/agentProfile/SectionCard';

import { initialsFrom, colorFor, isOnline, subtitleFor, timeAgo, formatTimeSince } from '../utils';
import EmptyInline from '../EmptyInline';

/* ─── InvitationRow (section-internal sub-component) ─────────────────
   Incoming connection request row for the "Received" tab. Avatar
   (with online dot), name, subtitle, "Received X days ago" line,
   optional mutual face-pile, optional inline message, Accept/Ignore. */
const InvitationRow = ({ invitation, connections, isLast, actionLoading, onAccept, onReject, onView }) => {
    const p = invitation.other_party || {};
    const color = colorFor(p.profile_id);
    const online = isOnline(p.profile_id);
    const mutualFaces = connections.slice(0, 3).map((c) => c.other_party).filter(Boolean);
    const isLoading = actionLoading === invitation.id;
    /* Defensive photo URL — covers field-name variations the API may return. */
    const photoUrl = p.profile_photo_url_full
        || p.profile_photo_url
        || p.profile_photo
        || p.avatar
        || p.photo_url
        || null;

    /* Whole row body is keyboard-clickable → navigates to the requester's
       profile. Accept / Ignore buttons stop propagation so they don't
       double-fire navigation. */
    const handleRowClick = () => p.profile_id && onView?.(p.profile_id);
    const handleRowKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick();
        }
    };

    return (
        <React.Fragment>
            {/* 3-column row: avatar | info | actions.
                Mobile (<md): actions stack vertically on the right, side-by-side
                on md+. PrimeFlex breakpoints, no custom CSS. */}
            <div
                className="flex align-items-start gap-3 py-3 px-2 border-round transition-colors transition-duration-150 hover:surface-50 cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={handleRowClick}
                onKeyDown={handleRowKeyDown}
            >
                <div className="relative flex-shrink-0">
                    <Avatar
                        label={initialsFrom(p.full_name)}
                        image={photoUrl}
                        shape="circle"
                        style={{
                            width: '3rem',
                            height: '3rem',
                            backgroundColor: `var(--${color}-100)`,
                            color: `var(--${color}-700)`,
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            border: '2px solid var(--surface-0)',
                            boxShadow: `0 0 0 2px var(--${color}-200)`,
                        }}
                    />
                    {online && (
                        <span
                            className="absolute border-circle"
                            style={{
                                width: '0.75rem',
                                height: '0.75rem',
                                bottom: 0,
                                right: 0,
                                backgroundColor: 'var(--green-500)',
                                border: '2px solid var(--surface-0)',
                            }}
                            aria-label="Online"
                        />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex align-items-baseline justify-content-between gap-2">
                        <span className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {p.full_name || 'Unknown'}
                        </span>
                        <span className="text-400 text-xs flex-shrink-0">
                            {timeAgo(invitation.created_at)}
                        </span>
                    </div>
                    <div className="text-600 text-xs line-height-3 mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {subtitleFor(p)}
                    </div>
                    {/* Explicit "Received X days ago" line — mirrors the
                        Sent tab's pattern. Uses requested_at when the API
                        provides it (server-side connection timestamp);
                        falls back to created_at. */}
                    <div className="flex align-items-center gap-1 mt-1 text-500 text-xs">
                        <i className="pi pi-clock" style={{ fontSize: '0.65rem' }} aria-hidden="true" />
                        Received {formatTimeSince(invitation.requested_at || invitation.created_at)}
                    </div>
                    {p.mutual_count > 0 && mutualFaces.length > 0 && (
                        <div className="flex align-items-center gap-2 mt-2">
                            <AvatarGroup>
                                {mutualFaces.map((m, i) => {
                                    const c = colorFor(m.profile_id);
                                    return (
                                        <Avatar
                                            key={m.profile_id || i}
                                            label={initialsFrom(m.full_name)}
                                            shape="circle"
                                            style={{
                                                width: '1.5rem',
                                                height: '1.5rem',
                                                backgroundColor: `var(--${c}-100)`,
                                                color: `var(--${c}-700)`,
                                                fontWeight: 700,
                                                fontSize: '0.55rem',
                                                border: '2px solid var(--surface-0)',
                                            }}
                                        />
                                    );
                                })}
                            </AvatarGroup>
                            <span className="text-500 text-xs">
                                <strong className="text-700">{p.mutual_count}</strong> mutual connection
                                {p.mutual_count > 1 ? 's' : ''}
                            </span>
                        </div>
                    )}
                    {invitation.connection_message && (
                        <div
                            className="text-700 text-xs line-height-3 mt-2 pl-2"
                            style={{
                                borderLeft: '2px solid var(--primary-300)',
                                fontStyle: 'italic',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {invitation.connection_message}
                        </div>
                    )}
                </div>

                {/* Actions column — always side-by-side, never stacked.
                    Mobile: icon-only (X / ✓) for compact tap targets.
                    Desktop: icon + label for clarity.
                    Color does the work: secondary outlined = Ignore,
                    primary filled = Accept. Industry-standard pattern.
                    onClick wrapper stops propagation so action clicks don't
                    also fire the row's navigate-to-profile handler. */}
                <div
                    className="flex gap-2 flex-shrink-0 align-self-center"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Button
                        label="Ignore"
                        icon="pi pi-times"
                        size="small"
                        severity="secondary"
                        outlined
                        rounded
                        disabled={isLoading}
                        onClick={(e) => { e.stopPropagation(); onReject(); }}
                        aria-label="Ignore invitation"
                        pt={{
                            label: { className: 'hidden md:inline ml-1' },
                        }}
                    />
                    <Button
                        label="Accept"
                        icon="pi pi-check"
                        size="small"
                        rounded
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={(e) => { e.stopPropagation(); onAccept(); }}
                        aria-label="Accept invitation"
                        pt={{
                            label: { className: 'hidden md:inline ml-1' },
                        }}
                    />
                </div>
            </div>
            {!isLast && <Divider className="my-0" />}
        </React.Fragment>
    );
};

/* ─── SentInvitationRow (section-internal sub-component) ─────────────
   Outgoing connection request that hasn't been accepted yet. Renders
   inside the "Sent" tab. Each row has a Pending status tag + Withdraw
   button (which fires onWithdraw → handleCancelSent →
   ConnectionService.removeConnection). */
const SentInvitationRow = ({ invitation, isLast, actionLoading, onWithdraw, onView }) => {
    const p = invitation.other_party || {};
    const color = colorFor(p.profile_id);
    const isLoading = actionLoading === invitation.id;
    const photoUrl = p.profile_photo_url_full
        || p.profile_photo_url
        || p.profile_photo
        || p.avatar
        || p.photo_url
        || null;

    /* Whole row body is keyboard-clickable → navigates to the recipient's
       profile. Withdraw button stops propagation so it doesn't double-fire. */
    const handleRowClick = () => p.profile_id && onView?.(p.profile_id);
    const handleRowKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick();
        }
    };

    return (
        <React.Fragment>
            <div
                className="flex align-items-center gap-3 py-3 px-2 border-round transition-colors transition-duration-150 hover:surface-50 cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={handleRowClick}
                onKeyDown={handleRowKeyDown}
            >
                <Avatar
                    label={initialsFrom(p.full_name)}
                    image={photoUrl}
                    shape="circle"
                    style={{
                        width: '3rem',
                        height: '3rem',
                        backgroundColor: `var(--${color}-100)`,
                        color: `var(--${color}-700)`,
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        flexShrink: 0,
                        border: '2px solid var(--surface-0)',
                        boxShadow: `0 0 0 2px var(--${color}-200)`,
                    }}
                />
                <div className="flex-1 min-w-0">
                    <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {p.full_name || 'Unknown'}
                    </div>
                    <div className="text-600 text-xs line-height-3 mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {subtitleFor(p)}
                    </div>
                    <div className="flex align-items-center gap-2 mt-2">
                        <Tag
                            value="Pending"
                            severity="warning"
                            icon="pi pi-clock"
                            style={{ fontSize: '0.6rem', padding: '0.15rem 0.45rem' }}
                        />
                        <span className="text-400 text-xs">
                            Sent {formatTimeSince(invitation.requested_at || invitation.created_at)}
                        </span>
                    </div>
                </div>
                <Button
                    label="Withdraw"
                    icon="pi pi-times"
                    size="small"
                    severity="secondary"
                    outlined
                    rounded
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={(e) => { e.stopPropagation(); onWithdraw(); }}
                    aria-label="Withdraw invitation"
                    pt={{
                        label: { className: 'hidden md:inline ml-1' },
                    }}
                    className="flex-shrink-0 align-self-center"
                />
            </div>
            {!isLast && <Divider className="my-0" />}
        </React.Fragment>
    );
};

/* ─── PendingTabContent (section-internal sub-component) ─────────────
   Shared body for both Received and Sent tabs.
     - Compact (default):  first 5 items + "Show more" text button.
     - Expanded:           continuous-list scroll container (no border,
                           no visible scrollbar). max-height tuned so
                           exactly 6 rows fit without scrolling. Items
                           7+ live below the fold and reveal on internal
                           scroll. Frosted PrimeReact ScrollTop FAB at
                           bottom-right (target="parent"), styling matches
                           SocialFeed. "Show less" text button below.
   Each tab passes its own expanded state so they're independent. */
const PendingTabContent = ({
    items, total, loading, loadingMore, hasMore,
    expanded, onExpand, onCollapse,
    scrollRef, sentinelRef,
    emptyIcon, emptyTitle, emptyDescription,
    renderItem,
}) => {
    if (loading && items.length === 0) {
        return (
            <div className="flex flex-column gap-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} width="100%" height="4rem" />)}
            </div>
        );
    }
    if (items.length === 0) {
        return (
            <EmptyInline icon={emptyIcon} title={emptyTitle} description={emptyDescription} />
        );
    }

    /* Compact mode: first 5 items + "Show more" text button (no count). */
    if (!expanded) {
        const preview = items.slice(0, 5);
        return (
            <>
                <ul className="list-none p-0 m-0">
                    {preview.map((it, i) => renderItem(it, i, preview))}
                </ul>
                {(items.length > 5 || total > items.length) && (
                    <div className="text-center pt-3 border-top-1 surface-border">
                        <Button
                            label="Show more"
                            icon="pi pi-chevron-down"
                            iconPos="right"
                            text
                            size="small"
                            onClick={onExpand}
                        />
                    </div>
                )}
            </>
        );
    }

    /* Expanded mode:
         - scroll container, no border, hidden scrollbar (CSS below)
         - frosted ScrollTop FAB anchored to container at bottom-right
         - "Show less" text button below the container */
    return (
        <>
            {/* Scoped style block — hides webkit scrollbar (Chrome/Safari)
                and styles the frosted ScrollTop FAB to match SocialFeed. */}
            <style>{`
                .lending-pending-scroll::-webkit-scrollbar { display: none; }
                .lending-pending-scrolltop {
                    bottom: 0.75rem !important;
                    right: 0.75rem !important;
                    background-color: rgba(120, 120, 128, 0.32) !important;
                    backdrop-filter: blur(12px) saturate(180%);
                    -webkit-backdrop-filter: blur(12px) saturate(180%);
                    border: 1px solid rgba(255, 255, 255, 0.4) !important;
                    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18) !important;
                    width: 2.5rem !important;
                    height: 2.5rem !important;
                }
                .lending-pending-scrolltop .pi {
                    color: #1f2937 !important;
                    font-size: 1rem !important;
                    font-weight: 700 !important;
                }
            `}</style>

            <div
                ref={scrollRef}
                className="lending-pending-scroll"
                style={{
                    maxHeight: '28rem',          // ~6 rows visible without scrolling
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    position: 'relative',
                    scrollbarWidth: 'none',      // Firefox
                    msOverflowStyle: 'none',     // IE / old Edge
                }}
            >
                <ul className="list-none p-0 m-0">
                    {items.map((it, i) => renderItem(it, i, items))}
                </ul>
                {hasMore && (
                    <div ref={sentinelRef} className="text-center py-3">
                        {loadingMore ? (
                            <span className="text-500 text-xs">
                                <i className="pi pi-spin pi-spinner mr-1" aria-hidden="true" />
                                Loading more…
                            </span>
                        ) : (
                            <span className="text-400 text-xs">Scroll for more</span>
                        )}
                    </div>
                )}
                {!hasMore && items.length > 5 && (
                    <div className="text-center py-3 text-400 text-xs">
                        You've reached the end · {total} total
                    </div>
                )}
                <ScrollTop
                    target="parent"
                    threshold={200}
                    icon="pi pi-arrow-up"
                    behavior="smooth"
                    className="lending-pending-scrolltop"
                />
            </div>

            {/* "Show less" — small text button below the container, always
                reachable. User is never trapped inside the scroll. */}
            <div className="text-center pt-3 border-top-1 surface-border">
                <Button
                    label="Show less"
                    icon="pi pi-chevron-up"
                    iconPos="right"
                    text
                    size="small"
                    onClick={onCollapse}
                />
            </div>
        </>
    );
};

/**
 * PendingInvitationsCard — Received | Sent TabView with both tabs
 * server-paginated (10 per page) and using IntersectionObserver-based
 * infinite scroll inside the expanded scroll container.
 *
 * The card ALWAYS renders so the section is discoverable even when there
 * are no invitations — each tab shows its own empty state in that case.
 *
 * State stays lifted to MyNetwork.jsx — this component is presentational
 * + receives its data + handlers as props.
 *
 * Props: see the inline destructure for the full list. Two parallel
 * groups (received* / sent*) plus shared controls (tab index, action
 * loading, optimistic-action callbacks).
 */
const PendingInvitationsCard = ({
    pendingCardRef,
    pendingTabIndex, onTabChange,

    pendingReceived, pendingReceivedTotal,
    pendingReceivedLoading, pendingReceivedLoadingMore,
    pendingReceivedHasMore,
    receivedExpanded, onReceivedExpand, onReceivedCollapse,
    receivedScrollRef, pendingReceivedSentinelRef,

    pendingSent, pendingSentTotal,
    pendingSentLoading, pendingSentLoadingMore,
    pendingSentHasMore,
    sentExpanded, onSentExpand, onSentCollapse,
    sentScrollRef, pendingSentSentinelRef,

    connections,
    actionLoading,
    onAccept, onReject, onWithdraw,
    onView,
}) => (
    <div id="pending-section" ref={pendingCardRef}>
        <SectionCard
            icon="pi-inbox"
            title="Invitations"
            subtitle={
                pendingTabIndex === 0
                    ? (pendingReceivedTotal === 0
                        ? 'No incoming requests right now'
                        : pendingReceivedTotal === 1
                            ? '1 person wants to connect'
                            : `${pendingReceivedTotal} people want to connect`)
                    : (pendingSentTotal === 0
                        ? "You haven't sent any pending requests"
                        : `${pendingSentTotal} pending request${pendingSentTotal === 1 ? '' : 's'} you've sent`)
            }
            className="mb-3"
        >
            <TabView
                activeIndex={pendingTabIndex}
                onTabChange={(e) => onTabChange(e.index)}
                pt={{
                    panelContainer: { className: 'p-0 pt-3' },
                }}
            >
                {/* RECEIVED tab */}
                <TabPanel header={`Received (${pendingReceivedTotal})`}>
                    <PendingTabContent
                        items={pendingReceived}
                        total={pendingReceivedTotal}
                        loading={pendingReceivedLoading}
                        loadingMore={pendingReceivedLoadingMore}
                        hasMore={pendingReceivedHasMore}
                        expanded={receivedExpanded}
                        onExpand={onReceivedExpand}
                        onCollapse={onReceivedCollapse}
                        scrollRef={receivedScrollRef}
                        sentinelRef={pendingReceivedSentinelRef}
                        emptyIcon="pi-inbox"
                        emptyTitle="No incoming invitations"
                        emptyDescription="When someone sends you a request, you'll see it here."
                        renderItem={(inv, i, arr) => (
                            <InvitationRow
                                key={inv.id}
                                invitation={inv}
                                connections={connections}
                                isLast={i === arr.length - 1}
                                actionLoading={actionLoading}
                                onAccept={() => onAccept(inv.id)}
                                onReject={() => onReject(inv.id)}
                                onView={onView}
                            />
                        )}
                    />
                </TabPanel>

                {/* SENT tab */}
                <TabPanel header={`Sent (${pendingSentTotal})`}>
                    <PendingTabContent
                        items={pendingSent}
                        total={pendingSentTotal}
                        loading={pendingSentLoading}
                        loadingMore={pendingSentLoadingMore}
                        hasMore={pendingSentHasMore}
                        expanded={sentExpanded}
                        onExpand={onSentExpand}
                        onCollapse={onSentCollapse}
                        scrollRef={sentScrollRef}
                        sentinelRef={pendingSentSentinelRef}
                        emptyIcon="pi-send"
                        emptyTitle="No pending sent"
                        emptyDescription="Requests you've sent that haven't been accepted yet will show up here."
                        renderItem={(inv, i, arr) => (
                            <SentInvitationRow
                                key={inv.id}
                                invitation={inv}
                                isLast={i === arr.length - 1}
                                actionLoading={actionLoading}
                                onWithdraw={() => onWithdraw(inv.id)}
                                onView={onView}
                            />
                        )}
                    />
                </TabPanel>
            </TabView>
        </SectionCard>
    </div>
);

export default React.memo(PendingInvitationsCard);
